import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { FirestoreGenerationStatus } from "reedy-data/models";
import { useSession } from "~/contexts/auth-context";
import { getEvaluations } from "~/integrations/firebase/collections/evaluations";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/button";
import { logError } from "~/shared/services/error-reporting";
import { useFeatureFlags } from "~/contexts/feature-flag-context";
import { EvaluationCard } from "~/components/evaluation-card";
import { Text } from "~/components/text";

export const EvaluationResults = () => {
  const { features } = useFeatureFlags();
  const { t } = useTranslation(["books"]);
  const { user, organisation } = useSession();
  const [showAllResults, setShowAllResults] = useState(false);

  const [evaluations, loading, errorLoadingEvaluations] = useCollectionData(
    getEvaluations(organisation.id),
  );

  if (errorLoadingEvaluations) {
    logError(errorLoadingEvaluations);
  }

  if (!features.evaluation) {
    return null;
  }
  if (loading) {
    return <div className="animate-pulse h-32 bg-secondary-100 rounded-md" />;
  }

  const sortedEvaluations =
    evaluations?.sort(
      (a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0),
    ) ?? [];

  const completedEvaluations = sortedEvaluations.filter(
    (evaluation) => evaluation.status === FirestoreGenerationStatus.COMPLETED,
  );

  const highlightText = user.isSuperAdmin
    ? t("books:evaluation.noResults.admin.highlight")
    : t("books:evaluation.noResults.user.highlight");
  const detailsText = user.isSuperAdmin
    ? t("books:evaluation.noResults.admin.details")
    : t("books:evaluation.noResults.user.details");

  const newestEvaluation = user.isSuperAdmin
    ? sortedEvaluations[0]
    : completedEvaluations[0];
  const moreEvaluations = user.isSuperAdmin
    ? sortedEvaluations
    : completedEvaluations;

  const hasMultipleResults = moreEvaluations && moreEvaluations.length > 1;

  return (
    <>
      <div className="space-y-4">
        <Text as="h2" variant="heading">
          {t("books:evaluation.results.title")}
        </Text>

        <div className="space-y-2">
          <div className="grid grid-cols-1 gap-4 grid-rows-[90px]">
            {!newestEvaluation && (
              <div className="flex-1 bg-secondary-25 rounded-xl flex items-center justify-center px-6 py-3 space-x-3">
                <Text as="div" variant="subtle">
                  <Text as="span" variant="detail">
                    {highlightText}
                  </Text>
                  {" - "}
                  <span>{detailsText}</span>
                </Text>
              </div>
            )}

            {newestEvaluation && (
              <EvaluationCard
                id={newestEvaluation.id}
                numberOfBooks={newestEvaluation.data.requestedProducts.length}
                showStatus={user.isSuperAdmin}
                status={newestEvaluation.status}
                createdAt={newestEvaluation.createdAt}
              />
            )}
          </div>

          {hasMultipleResults && (
            <Button
              variant="ghost"
              size="small"
              className="text-primary-600 text-sm"
              label={t("books:evaluation.seeHistory")}
              onClick={() => setShowAllResults(true)}
              icon="arrow-right"
              iconPosition="right"
            />
          )}
        </div>
      </div>

      <Dialog open={showAllResults} onOpenChange={setShowAllResults}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("books:evaluation.allResults")}</DialogTitle>
            <DialogDescription>
              {t("books:evaluation.allResultsDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[400px] grid grid-cols-1 gap-3 bg-secondary-25 overflow-y-auto p-3 rounded-2xl">
            {moreEvaluations.map((evaluation) => (
              <EvaluationCard
                key={evaluation.id}
                id={evaluation.id}
                numberOfBooks={evaluation.data.requestedProducts.length}
                status={evaluation.status}
                showStatus={user.isSuperAdmin}
                createdAt={evaluation.createdAt}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
